import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Package, RefreshCw, LogOut, MapPin, Building2, 
  Calendar, Clock, User, Phone, ChevronDown, ChevronUp, FileText,
  Truck, Timer, ExternalLink, ChefHat
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import LanguageSwitcher from '../components/LanguageSwitcher';

function MyOrders({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeaders() });
      setOrders(response.data);
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = (order) => {
    const reorderItems = order.items.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
    }));
    
    localStorage.setItem('cart', JSON.stringify(reorderItems));
    toast.success(t('catalog.addedToCart'));
    navigate(`/${currentLang}`);
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('ru-RU', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'отклонён':
      case 'cancelled':
        return 'bg-red-500 text-white';
      case 'подтверждён':
      case 'confirmed':
      case 'delivered':
        return 'bg-green-500 text-white';
      case 'in production':
      case 'в производстве':
        return 'bg-blue-500 text-white';
      case 'ready':
      case 'готов':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-emerald-600 text-white';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={`/${currentLang}`} className="flex items-center">
              <img 
                src="/logo.jpg" 
                alt="Ainanan" 
                className="h-10 object-contain"
              />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to={`/${currentLang}`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.catalog')}</Link>
              <Link to={`/${currentLang}/about`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.about')}</Link>
              <Link to={`/${currentLang}/contacts`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.contacts')}</Link>
            </nav>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.backToCatalog') || 'Вернуться в каталог'}
        </Link>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Link to={`/${currentLang}/profile`}>
            <Button variant="outline">{t('profile.title') || 'Профиль'}</Button>
          </Link>
          <Link to={`/${currentLang}/settings`}>
            <Button variant="outline">{t('settings.title') || 'Настройки'}</Button>
          </Link>
          <Link to={`/${currentLang}/orders`}>
            <Button variant="default" className="bg-emerald-700 hover:bg-emerald-800">
              {t('common.myOrders')}
            </Button>
          </Link>
          <Link to={`/${currentLang}/addresses`}>
            <Button variant="outline">{t('common.myAddresses')}</Button>
          </Link>
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/legal-entities`}>
              <Button variant="outline">{t('legalEntities.title') || 'Юрлица'}</Button>
            </Link>
          )}
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/b2b/networks`}>
              <Button variant="outline">Мои сети</Button>
            </Link>
          )}
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('myOrders.title') || 'Мои заказы'}</h1>
        <p className="text-gray-600 mb-8">{t('myOrders.subtitle') || 'История ваших заказов'}</p>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">{t('common.loading')}</div>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-xl text-gray-600 mb-4">{t('myOrders.noOrders') || 'У вас пока нет заказов'}</p>
                <Link to={`/${currentLang}`}>
                  <Button className="bg-emerald-700 hover:bg-emerald-800">
                    {t('common.catalog') || 'Перейти в каталог'}
                  </Button>
                </Link>
              </div>
            ) : (
              orders.map(order => (
                <Card key={order.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap justify-between items-start gap-2">
                      <div>
                        <CardTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-emerald-600" />
                          {t('myOrders.order') || 'Заказ'} #{order.order_number}
                        </CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('myOrders.created') || 'Создан'}: {formatDateTime(order.created_at)}
                        </p>
                      </div>
                      <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    {/* Order Summary - Always visible */}
                    <div className="flex flex-wrap items-center justify-between gap-4 py-3 border-b">
                      <div className="flex items-center gap-6 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          {order.items?.length || 0} {t('myOrders.positions') || 'позиций'}
                        </span>
                        {order.delivery_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(order.delivery_date)}
                          </span>
                        )}
                        {order.delivery_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {order.delivery_time}
                          </span>
                        )}
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        {(order.final_amount || order.total_amount)?.toLocaleString()} ₸
                        {order.delivery_fee > 0 && (
                          <span className="text-sm font-normal text-gray-500 ml-1">(+доставка)</span>
                        )}
                      </div>
                    </div>

                    {/* Expand/Collapse Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                      onClick={() => toggleOrderDetails(order.id)}
                    >
                      {expandedOrder === order.id ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-2" />
                          {t('myOrders.hideDetails') || 'Скрыть детали'}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-2" />
                          {t('myOrders.showDetails') || 'Показать детали'}
                        </>
                      )}
                    </Button>

                    {/* Expanded Details */}
                    {expandedOrder === order.id && (
                      <div className="mt-4 space-y-4">
                        {/* Order Items */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <Package className="w-4 h-4 text-emerald-600" />
                            {t('myOrders.orderedItems') || 'Заказанные товары'}
                          </h4>
                          <div className="space-y-2">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                                <div>
                                  <span className="font-medium text-gray-900">{item.product_name}</span>
                                  <span className="text-sm text-gray-500 ml-2">× {item.quantity}</span>
                                  <span className="text-sm text-gray-400 ml-2">({item.unit_price?.toLocaleString()} ₸/{t('catalog.perUnit') || 'шт'})</span>
                                </div>
                                <span className="font-medium text-gray-900">{item.total?.toLocaleString()} ₸</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Delivery Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Address */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-emerald-600" />
                              {t('myOrders.deliveryAddress') || 'Адрес доставки'}
                            </h4>
                            <p className="text-gray-600">{order.user_address || '—'}</p>
                            {order.user_phone && (
                              <p className="text-gray-500 text-sm mt-1 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {order.user_phone}
                              </p>
                            )}
                          </div>

                          {/* Customer Info */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                              <User className="w-4 h-4 text-emerald-600" />
                              {t('myOrders.customerInfo') || 'Информация о заказчике'}
                            </h4>
                            <p className="text-gray-600">{order.user_name || '—'}</p>
                            <p className="text-gray-500 text-sm">{order.user_email}</p>
                            <Badge className="mt-2 bg-gray-200 text-gray-700 text-xs">
                              {order.user_role === 'B2B' ? 'Бизнес' : 'Частное лицо'}
                            </Badge>
                          </div>
                        </div>

                        {/* Delivery Schedule */}
                        {(order.delivery_date || order.delivery_time) && (
                          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-emerald-600" />
                              {t('myOrders.deliverySchedule') || 'Дата и время доставки'}
                            </h4>
                            <div className="flex items-center gap-4 text-gray-700">
                              {order.delivery_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4 text-emerald-600" />
                                  {formatDate(order.delivery_date)}
                                </span>
                              )}
                              {order.delivery_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4 text-emerald-600" />
                                  {order.delivery_time}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Legal Entity (B2B only) */}
                        {order.legal_entity_info && (
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-blue-600" />
                              {t('myOrders.legalEntity') || 'Юридическое лицо'}
                            </h4>
                            <div className="space-y-1 text-gray-700">
                              <p className="font-medium">{order.legal_entity_info.company_name}</p>
                              <p className="text-sm">БИН/ИИН: {order.legal_entity_info.bin_iin}</p>
                              <p className="text-sm text-gray-500">{order.legal_entity_info.legal_address}</p>
                              {order.legal_entity_info.bank_name && (
                                <p className="text-sm text-gray-500 mt-2">
                                  {order.legal_entity_info.bank_name} • {order.legal_entity_info.bank_account}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Delivery Tracking & Time Estimation */}
                        {order.delivery_type === 'delivery' && (
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                              <Truck className="w-4 h-4 text-blue-600" />
                              Статус доставки
                            </h4>
                            
                            {/* Time Estimation */}
                            {(order.estimated_cooking_time || order.estimated_delivery_time) && (
                              <div className="flex flex-wrap gap-4 mb-3 text-sm">
                                {order.estimated_cooking_time && (
                                  <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg">
                                    <ChefHat className="w-4 h-4 text-orange-500" />
                                    <span>Готовка: <b>{order.estimated_cooking_time} мин</b></span>
                                  </div>
                                )}
                                {order.estimated_delivery_time && (
                                  <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg">
                                    <Truck className="w-4 h-4 text-blue-500" />
                                    <span>Доставка: <b>~{order.estimated_delivery_time} мин</b></span>
                                  </div>
                                )}
                                {order.estimated_cooking_time && order.estimated_delivery_time && (
                                  <div className="flex items-center gap-2 bg-emerald-100 px-3 py-2 rounded-lg text-emerald-800">
                                    <Timer className="w-4 h-4" />
                                    <span>Итого: <b>~{order.estimated_cooking_time + order.estimated_delivery_time} мин</b></span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Delivery Fee */}
                            {order.delivery_fee > 0 && (
                              <p className="text-sm text-gray-600 mb-3">
                                Стоимость доставки: <b>{order.delivery_fee.toLocaleString()} ₸</b>
                              </p>
                            )}
                            
                            {/* Tracking Link */}
                            {order.tracking_link ? (
                              <a
                                href={order.tracking_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
                              >
                                <ExternalLink className="w-5 h-5" />
                                <span className="font-medium">Отследить курьера</span>
                              </a>
                            ) : (
                              <div className="text-sm text-gray-500 bg-white px-4 py-3 rounded-lg text-center">
                                {order.status === 'Delivering' ? (
                                  'Курьер в пути. Ссылка для отслеживания скоро появится.'
                                ) : (
                                  'Ссылка для отслеживания появится когда заказ будет отправлен'
                                )}
                              </div>
                            )}
                            
                            {/* Dispatched time */}
                            {order.courier_dispatched_at && (
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                Курьер отправлен: {new Date(order.courier_dispatched_at).toLocaleString('ru-RU')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Delivery Notes */}
                        {order.delivery_notes && (
                          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                            <h4 className="font-medium text-gray-900 mb-2">
                              {t('myOrders.deliveryNotes') || 'Комментарий к доставке'}
                            </h4>
                            <p className="text-gray-600 italic">&quot;{order.delivery_notes}&quot;</p>
                          </div>
                        )}

                        {/* Total & Reorder */}
                        <div className="pt-4 border-t flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <span className="text-gray-600">{t('myOrders.total') || 'Итого'}:</span>
                            <span className="text-2xl font-bold text-gray-900 ml-2">{order.total_amount?.toLocaleString()} ₸</span>
                          </div>
                          <Button
                            onClick={() => handleReorder(order)}
                            className="bg-emerald-700 hover:bg-emerald-800"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {t('myOrders.reorder') || 'Повторить заказ'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default MyOrders;
